import { useCallback, useRef, useState } from 'react'
import { fetchCategoryV3 } from 'lib/dealhunt/client'
import { CATEGORY_DEFAULT_SORT } from 'lib/dealhunt/constants'
import { toUrlParams, type CategoryFilterState } from 'lib/dealhunt/filters'
import type {
  CategoryFilterModel,
  CategorySibling,
  CategorySummary,
  DealHuntCategoryHit,
  DealHuntCategoryV3,
  DealHuntSponsoredSlot,
} from 'lib/dealhunt/types'

/** What the rail + grid render: the `/api/category/v3` response, flattened. */
export interface CategoryBrowseData {
  hits: DealHuntCategoryHit[]
  nbHits: number
  page: number
  nbPages: number
  summary: CategorySummary
  filters: CategoryFilterModel
  siblings: CategorySibling[]
  /** Page 0's sponsored slot (contract B3/C1), kept across "Load more" — later pages carry
   *  none, and the grid dedupes them against it. Null when none. */
  sponsoredSlot: DealHuntSponsoredSlot | null
  /** SSR shipped `filters.more` empty (see `deferDrawerModel`); `ensureFilterModel`
   *  fills it on first drawer open. Never set on client-fetched data. */
  moreDeferred?: boolean
}

/**
 * Strip the "All filters" drawer model from the SSR payload. `filters.more` is ~85% of
 * `__NEXT_DATA__` on parent nodes (320 of 375 KB raw on /product/audio/, measured
 * 2026-08-22) and the drawer is closed on first paint — the rail, tokens, search
 * suggestions and `summary.moreTotal` ("All filters · 93 more") are all kept.
 */
export function deferDrawerModel(data: CategoryBrowseData): CategoryBrowseData {
  if (data.filters.more.length === 0) {
    return data
  }
  return { ...data, filters: { ...data.filters, more: [] }, moreDeferred: true }
}

export function browseDataFromV3(res: DealHuntCategoryV3): CategoryBrowseData {
  return {
    hits: res.listing.hits,
    nbHits: res.listing.nbHits,
    page: res.listing.page,
    nbPages: res.listing.nbPages,
    summary: res.summary,
    filters: res.filters,
    siblings: res.siblings,
    // `?? null`: absent until DealHunt deploys B3 and on every page past 0; gSSP props cannot
    // carry `undefined`.
    sponsoredSlot: res.sponsoredSlot ?? null,
  }
}

/**
 * A landed response into the grid's data: a refetch (filter / sort) replaces it whole, its
 * page-0 sponsored slot included; "Load more" appends hits and never brings, nor clears, the
 * sponsored slot — page 0's stays, so the grid keeps deduping later pages against it.
 */
export function mergeBrowsePage(
  prev: CategoryBrowseData,
  res: CategoryBrowseData,
  append: boolean
): CategoryBrowseData {
  if (!append) {
    return res
  }
  return { ...res, hits: [...prev.hits, ...res.hits], sponsoredSlot: prev.sponsoredSlot }
}

/**
 * Client-side controller for the model-driven category rail. Seeded with SSR
 * data; on filter/sort change refetches `/api/category/v3`, appends on "load
 * more", and mirrors filters into the URL.
 */
export function useCategoryBrowse(
  nodeId: string,
  initialFilters: CategoryFilterState,
  initialData: CategoryBrowseData
): {
  filters: CategoryFilterState
  data: CategoryBrowseData
  isLoading: boolean
  isLoadingMore: boolean
  applyFilters: (next: CategoryFilterState) => void
  loadMore: () => void
  /** Fetch the deferred drawer model (`filters.more`) for the current filters — call on
   *  drawer open / hover. No-op once present or after any client refetch. */
  ensureFilterModel: () => void
  /**
   * Bumps once per RESULTS response (filter change / load more) — the analytics
   * `list_view` trigger. Deliberately not `data` itself: `ensureFilterModel` also
   * replaces `data` (drawer model only, same hits), which is not a new list view.
   */
  responseSeq: number
} {
  const [filters, setFilters] = useState(initialFilters)
  const [data, setData] = useState(initialData)
  const [responseSeq, setResponseSeq] = useState(0)
  const [isLoading, setLoading] = useState(false)
  const [isLoadingMore, setLoadingMore] = useState(false)
  const abortRef = useRef<AbortController | null>(null)
  const ensuringRef = useRef(false)

  const syncUrl = useCallback((next: CategoryFilterState) => {
    if (typeof window === 'undefined') {
      return
    }
    const qs = toUrlParams(next, CATEGORY_DEFAULT_SORT).toString()
    window.history.replaceState(null, '', window.location.pathname + (qs ? `?${qs}` : ''))
  }, [])

  const run = useCallback(
    async (next: CategoryFilterState, append: boolean) => {
      abortRef.current?.abort()
      const ctrl = new AbortController()
      abortRef.current = ctrl
      if (append) {
        setLoadingMore(true)
      } else {
        setLoading(true)
      }
      setFilters(next)
      syncUrl(next)
      try {
        const v3 = await fetchCategoryV3(nodeId, next, ctrl.signal)
        if (v3) {
          const res = browseDataFromV3(v3)
          setData(prev => mergeBrowsePage(prev, res, append))
          setResponseSeq(n => n + 1)
        }
      } catch {
        // Aborted or failed — keep the previous data on screen.
      } finally {
        if (abortRef.current === ctrl) {
          setLoadingMore(false)
          setLoading(false)
        }
      }
    },
    [nodeId, syncUrl]
  )

  const applyFilters = useCallback(
    (next: CategoryFilterState) => {
      void run({ ...next, page: 0 }, false)
    },
    [run]
  )

  const loadMore = useCallback(() => {
    if (data.page + 1 >= data.nbPages) {
      return
    }
    void run({ ...filters, page: data.page + 1 }, true)
  }, [run, filters, data.page, data.nbPages])

  const ensureFilterModel = useCallback(() => {
    if (!data.moreDeferred || ensuringRef.current) {
      return
    }
    ensuringRef.current = true
    fetchCategoryV3(nodeId, { ...filters, page: 0 })
      .then(v3 => {
        if (v3) {
          // Only fill a still-deferred model: a filter change in flight replaces the
          // whole data (full model, moreDeferred unset) and must win.
          setData(prev => {
            if (!prev.moreDeferred) {
              return prev
            }
            return { ...prev, filters: v3.filters, moreDeferred: false }
          })
        }
      })
      .catch(() => {
        // Leave it deferred: the next open retries, and any filter change refetches in full.
      })
      .finally(() => {
        ensuringRef.current = false
      })
  }, [data.moreDeferred, filters, nodeId])

  return {
    filters,
    data,
    isLoading,
    isLoadingMore,
    applyFilters,
    loadMore,
    ensureFilterModel,
    responseSeq,
  }
}
